import { RawTextCell } from "@/components/shared/cells/text";
import type { InvoiceRowData } from "@/components/shared/data-table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePermissions } from "@/hooks/usePermissions";
import { InvoiceStatus } from "@/lib/constants/invoiceStatus";
import { cn } from "@/lib/utils";
import { useTenant } from "@/providers/session";
import type { CellContext } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

export const DescriptionCell = ({
  row,
}: CellContext<InvoiceRowData, unknown>) => {
  const { t } = useTranslation();
  const { isSuperAdmin } = usePermissions();
  const { isOcrReviewRequired } = useTenant();

  if (row.original.invoice.status === InvoiceStatus.NEW) {
    return null;
  }

  if (!row.original.invoice.isBooking) {
    return <RawTextCell value={row.original.invoice.documentTypeSummary} />;
  }

  if (
    (isOcrReviewRequired &&
      row.original.invoice.ocrReviewed === false &&
      !isSuperAdmin) ||
    row.original.invoice.splitPdf === true ||
    row.original.invoice.status === InvoiceStatus.ERROR
  ) {
    return (
      <div className="h-10">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              style={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                maxWidth: "100%",
              }}
              asChild
            >
              <span
                className={cn(
                  "flex-1 overflow-hidden whitespace-nowrap text-ellipsis",
                  "text-gray-500",
                )}
              >
                {row.original.invoice.status === InvoiceStatus.ERROR &&
                  t("invoice_error_description")}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {row.original.invoice.status === InvoiceStatus.ERROR &&
                  `${t("invoice_error_description")}...`}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  const displayText = row.original?.invoice?.aiSummary ?? "Unbekannt";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          style={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            maxWidth: "100%",
          }}
          asChild
        >
          <span>{displayText}</span>
        </TooltipTrigger>
        <TooltipContent>
          {/* TODO: Bring this back later in the game */}
          {/* {Array.from(descriptions).map((desc, index) => (
            <div key={index} style={{ marginBottom: "5px" }}>
              <p>{desc}</p>
            </div>
          ))} */}
          {displayText}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
